/**
 * The ticket panel under the shell-free routes (#1170): beside the icon rail,
 * the context taken from the route, "Meine Tickets" at `/tickets?reportId=…`
 * (the standalone page of #1169). No window manager here — see
 * `src/test/shellIndependence.test.ts`.
 *
 * `/tickets` shows only the person's own tickets. Someone else's similar
 * ticket (only admins see those) opens the admin view in the desktop, in a new
 * tab so the draft stays; without the desktop it has no "Öffnen" (#1191).
 */
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { STANDALONE_ROUTES } from '@/hooks/useAppHost'
import { deepLinkService } from '@/services/deep-link-service'
import { useStandaloneNotificationHost } from '../notifications/notificationHost'
import { TicketPanel } from './TicketPanel'
import { standaloneSystemFor, type TicketContextSource } from './ticketContext'

/** The standalone page of "Meine Tickets" (#1169). */
export const STANDALONE_TICKETS_ROUTE = '/tickets'

/** The admin view of a ticket: Cortex → Bug-Reports in the desktop. */
export function adminTicketUrl(reportId: string): string {
  return `/desktop${deepLinkService.generateUrl([{ appId: 'cortex', params: { tab: 'bug-reports', reportId } }])}`
}

function openAdminTicket(reportId: string) {
  window.open(adminTicketUrl(reportId), '_blank', 'noopener')
}

export function StandaloneTicketPanel() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const hasDesktop = useStandaloneNotificationHost()?.hasDesktop ?? false

  const readContextSource = useCallback((): TicketContextSource => {
    const appId = Object.entries(STANDALONE_ROUTES).find(([, route]) => route === location.pathname)?.[0]
    return {
      system: standaloneSystemFor(location.pathname),
      app: appId ?? null,
      window: appId ? t(`apps.${appId}`) : null,
      route: `${location.pathname}${location.search}`,
    }
  }, [location.pathname, location.search, t])

  const openMyTickets = useCallback(
    (reportId: string) => navigate(`${STANDALONE_TICKETS_ROUTE}?reportId=${encodeURIComponent(reportId)}`),
    [navigate],
  )

  return (
    <TicketPanel
      anchor="rail-left"
      system="chat"
      readContextSource={readContextSource}
      onOpenMyTickets={openMyTickets}
      onOpenOthersTicket={hasDesktop ? openAdminTicket : undefined}
    />
  )
}
